import { Fragment, useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useNotify, useTranslate } from 'react-admin';
import { PermissionButton } from 'src/routes/components';
import {
  NotificationCategory,
  NotificationCategoryKind,
  NotificationCategoryTranslation,
  arrangeNotificationCategories,
  categoryAncestors,
  listAllNotificationCategories,
  createNotificationCategory,
  updateNotificationCategory,
  deleteNotificationCategory,
  sortCategoryTree,
} from '../../../services/notificationConfigsService';

/** Casbin resource guarding every /notification-configs write. */
const PERMISSION_RESOURCE = 'notification-configs';

/** Columns of a category table, which the full-width loading/empty/expand rows span. */
const COLUMN_COUNT = 10;

const KIND_COLOR: Record<NotificationCategoryKind, 'info' | 'secondary'> = {
  GROUP: 'info',
  PREFERENCE: 'secondary',
};

const LANGUAGE_CHOICES = [
  'vi',
  'en',
  'es',
  'hi',
  'zh',
  'zh-Hant',
  'ja',
  'pt',
  'fr',
  'de',
  'it',
  'tr',
  'ko',
  'ar',
];

// ── Translations helpers ──────────────────────────────────────────────────────

/** All 14 fixed languages seeded from the record, followed by any custom-language rows. */
const seedTranslations = (
  source?: NotificationCategoryTranslation[] | null
): NotificationCategoryTranslation[] => {
  const fixed = LANGUAGE_CHOICES.map((languageCode) => {
    const existing = source?.find((tr) => tr.languageCode === languageCode);
    return {
      languageCode,
      name: existing?.name ?? '',
      description: existing?.description ?? '',
    };
  });
  const custom = (source ?? []).filter((tr) => !LANGUAGE_CHOICES.includes(tr.languageCode));
  return [...fixed, ...custom];
};

/** Keep only rows with a language code and some content; trim the code. */
const cleanTranslations = (
  translations: NotificationCategoryTranslation[]
): NotificationCategoryTranslation[] =>
  translations
    .filter((tr) => tr.languageCode.trim() && (tr.name.trim() || tr.description.trim()))
    .map((tr) => ({
      languageCode: tr.languageCode.trim(),
      name: tr.name,
      description: tr.description,
    }));

const isCustomRow = (index: number) => index >= LANGUAGE_CHOICES.length;

const TranslationsTable = ({
  translations,
  onChange,
}: {
  translations: NotificationCategoryTranslation[];
  onChange: (next: NotificationCategoryTranslation[]) => void;
}) => {
  const t = useTranslate();

  const setTranslation = (
    index: number,
    field: 'languageCode' | 'name' | 'description',
    value: string
  ) => onChange(translations.map((tr, i) => (i === index ? { ...tr, [field]: value } : tr)));

  const addCustomTranslation = () =>
    onChange([...translations, { languageCode: '', name: '', description: '' }]);

  const removeTranslation = (index: number) => onChange(translations.filter((_, i) => i !== index));

  // Spreadsheet-style paste: a tab/newline-delimited block (e.g. copied from Excel) fills down and
  // across from the focused cell. Pasting into a language cell may also append custom rows.
  const handlePaste = (
    startIndex: number,
    startField: 'languageCode' | 'name' | 'description',
    e: React.ClipboardEvent
  ) => {
    const text = e.clipboardData.getData('text');
    if (!text || (!text.includes('\t') && !text.includes('\n'))) return; // single value → default paste
    e.preventDefault();

    const grid = text
      .replace(/\r/g, '')
      .replace(/\n+$/, '')
      .split('\n')
      .map((line) => line.split('\t'));

    const fields =
      startField === 'languageCode'
        ? (['languageCode', 'name', 'description'] as const)
        : (['name', 'description'] as const);
    const startCol = (fields as readonly string[]).indexOf(startField);

    const next = translations.map((tr) => ({ ...tr }));
    grid.forEach((cells, r) => {
      let target = next[startIndex + r];
      if (!target) {
        // Only grow the table when the paste carries a language code (custom rows).
        if (startField !== 'languageCode') return;
        target = { languageCode: '', name: '', description: '' };
        next.push(target);
      }
      cells.forEach((val, c) => {
        const field = fields[startCol + c];
        if (!field) return;
        target[field] = field === 'languageCode' ? val.trim() : val;
      });
    });
    onChange(next);
  };

  return (
    <>
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: 120 }}>
                {t('module.notificationConfigs.languageCode', { _: 'Language' })}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, width: 280 }}>
                {t('module.notificationConfigs.translationName', { _: 'Name' })}
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                {t('module.notificationConfigs.description', { _: 'Description' })}
              </TableCell>
              <TableCell sx={{ width: 48 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {translations.map((tr, index) => {
              const custom = isCustomRow(index);
              return (
                <TableRow key={custom ? `custom-${index}` : tr.languageCode}>
                  <TableCell
                    sx={{ fontFamily: 'monospace', verticalAlign: 'top', pt: custom ? 0.8 : 2.5 }}
                  >
                    {custom ? (
                      <TextField
                        value={tr.languageCode}
                        onChange={(e) =>
                          setTranslation(index, 'languageCode', e.target.value.trim())
                        }
                        onPaste={(e) => handlePaste(index, 'languageCode', e)}
                        size="small"
                        fullWidth
                        variant="standard"
                        placeholder={t('module.notificationConfigs.languageCode', {
                          _: 'Language',
                        })}
                      />
                    ) : (
                      tr.languageCode
                    )}
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={tr.name}
                      onChange={(e) => setTranslation(index, 'name', e.target.value)}
                      onPaste={(e) => handlePaste(index, 'name', e)}
                      size="small"
                      fullWidth
                      variant="standard"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={tr.description}
                      onChange={(e) => setTranslation(index, 'description', e.target.value)}
                      onPaste={(e) => handlePaste(index, 'description', e)}
                      size="small"
                      fullWidth
                      multiline
                      variant="standard"
                    />
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    {custom && (
                      <Tooltip title={t('actions.delete')} arrow>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeTranslation(index)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
        <Button size="small" startIcon={<AddIcon />} onClick={addCustomTranslation}>
          {t('module.notificationConfigs.addTranslation', { _: 'Add language' })}
        </Button>
        <Typography variant="caption" color="text.secondary">
          {t('module.notificationConfigs.pasteFromExcelHint', {
            _: 'Tip: paste from Excel into a cell to fill multiple rows',
          })}
        </Typography>
      </Stack>
    </>
  );
};

// ── Category Form Dialog ──────────────────────────────────────────────────────

const CategoryFormDialog = ({
  mode,
  record,
  categories,
  defaultParentId = null,
  onClose,
  onSaved,
}: {
  mode: 'create' | 'edit';
  record?: NotificationCategory;
  /** Every category in tree order; the parent choices come from here. */
  categories: NotificationCategory[];
  defaultParentId?: string | null;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const t = useTranslate();
  const notify = useNotify();
  const [form, setForm] = useState({
    categoryCode: record?.categoryCode ?? '',
    categoryName: record?.categoryName ?? '',
    description: record?.description ?? '',
    defaultValue: record?.defaultValue ?? false,
    enabled: record?.enabled ?? true,
    // '' is the top level: the select needs a string value.
    parentId: (record ? record.parentId : defaultParentId) ?? '',
  });
  const [translations, setTranslations] = useState<NotificationCategoryTranslation[]>(() =>
    seedTranslations(record?.translations)
  );
  const [loading, setLoading] = useState(false);

  const ancestors = useMemo(() => categoryAncestors(categories), [categories]);
  // A category cannot be moved under itself or under one of its own sub-categories.
  const parentChoices = useMemo(
    () =>
      record
        ? categories.filter(
            (c) => c.id !== record.id && !ancestors.get(c.id)?.some((a) => a.id === record.id)
          )
        : categories,
    [categories, ancestors, record]
  );

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const cleanedTranslations = cleanTranslations(translations);

      if (mode === 'create') {
        await createNotificationCategory({
          categoryCode: form.categoryCode,
          categoryName: form.categoryName,
          description: form.description,
          defaultValue: form.defaultValue,
          enabled: form.enabled,
          version: 0,
          translations: cleanedTranslations,
          parentId: form.parentId || undefined,
        });
        notify(t('module.notificationConfigs.categoryCreated', { _: 'Category created' }), {
          type: 'success',
          autoHideDuration: 1500,
        });
      } else {
        await updateNotificationCategory(record!.id, {
          categoryName: form.categoryName,
          description: form.description,
          defaultValue: form.defaultValue,
          enabled: form.enabled,
          translations: cleanedTranslations,
        });
        const parentId = form.parentId || null;
        if (parentId !== (record!.parentId ?? null)) {
          // The update endpoint cannot reparent, so arrange moves it after its new siblings.
          const siblings = categories.filter((c) => (c.parentId ?? null) === parentId);
          await arrangeNotificationCategories({
            items: [
              {
                id: record!.id,
                parentId,
                sortOrder: siblings.length
                  ? Math.max(...siblings.map((c) => c.sortOrder ?? 0)) + 1
                  : 0,
              },
            ],
          });
        }
        notify(t('module.notificationConfigs.categoryUpdated', { _: 'Category updated' }), {
          type: 'success',
          autoHideDuration: 1500,
        });
      }
      onSaved();
      onClose();
    } catch (err: any) {
      notify(err?.message ?? t('ra.notification.http_error'), { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DialogTitle>
        {mode === 'create'
          ? t('module.notificationConfigs.createCategory', { _: 'Create Category' })
          : t('module.notificationConfigs.editCategory', { _: 'Edit Category' })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            select
            label={t('module.notificationConfigs.parentCategory', { _: 'Parent Category' })}
            value={form.parentId}
            onChange={(e) => setForm((p) => ({ ...p, parentId: e.target.value }))}
            size="small"
            fullWidth
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">
              <em>{t('module.notificationConfigs.noParentCategory', { _: 'None (top level)' })}</em>
            </MenuItem>
            {parentChoices.map((c) => (
              <MenuItem
                key={c.id}
                value={c.id}
                sx={{ pl: 2 + (ancestors.get(c.id)?.length ?? 0) * 2 }}
              >
                <Box component="span" sx={{ fontFamily: 'monospace' }}>
                  {c.categoryCode}
                </Box>
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  {c.categoryName}
                </Typography>
              </MenuItem>
            ))}
          </TextField>
          {mode === 'create' && (
            <TextField
              label={t('module.notificationConfigs.categoryCode', { _: 'Category Code' })}
              value={form.categoryCode}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  categoryCode: e.target.value.replace(/\s+/g, ''),
                }))
              }
              size="small"
              fullWidth
              required
            />
          )}
          <TextField
            label={t('module.notificationConfigs.categoryName', { _: 'Category Name' })}
            value={form.categoryName}
            onChange={(e) => setForm((p) => ({ ...p, categoryName: e.target.value }))}
            size="small"
            fullWidth
            required
          />
          <TextField
            label={t('module.notificationConfigs.description', { _: 'Description' })}
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            size="small"
            fullWidth
            multiline
            minRows={2}
          />
          <Stack direction="row" spacing={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.defaultValue}
                  onChange={(e) => setForm((p) => ({ ...p, defaultValue: e.target.checked }))}
                />
              }
              label={t('module.notificationConfigs.defaultValue', { _: 'Default Value' })}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.enabled}
                  onChange={(e) => setForm((p) => ({ ...p, enabled: e.target.checked }))}
                />
              }
              label={t('module.notificationConfigs.enabled', { _: 'Enabled' })}
            />
          </Stack>

          <Typography variant="subtitle2" color="text.secondary" sx={{ pt: 1 }}>
            {t('module.notificationConfigs.translations', { _: 'Translations' })}
          </Typography>
          <TranslationsTable translations={translations} onChange={setTranslations} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={loading}>
          {t('actions.cancel')}
        </Button>
        <PermissionButton
          resource={PERMISSION_RESOURCE}
          action={mode === 'create' ? 'create' : 'update'}
        >
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !form.categoryName || (mode === 'create' && !form.categoryCode)}
          >
            {t('actions.save', { _: 'Save' })}
          </Button>
        </PermissionButton>
      </DialogActions>
    </>
  );
};

// ── Delete Confirm Dialog ─────────────────────────────────────────────────────

const DeleteConfirmDialog = ({
  record,
  onClose,
  onDeleted,
}: {
  record: NotificationCategory;
  onClose: () => void;
  onDeleted: () => void;
}) => {
  const t = useTranslate();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    setLoading(true);
    try {
      await deleteNotificationCategory(record.id);
      notify(t('module.notificationConfigs.categoryDeleted', { _: 'Category deleted' }), {
        type: 'success',
        autoHideDuration: 1500,
      });
      onDeleted();
      onClose();
    } catch (err: any) {
      notify(err?.message ?? t('ra.notification.http_error'), { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DialogTitle>
        {t('module.notificationConfigs.deleteCategory', { _: 'Delete Category' })}
      </DialogTitle>
      <DialogContent>
        <Typography>
          {t('module.notificationConfigs.deleteCategoryConfirm', {
            _: `Delete "${record.categoryCode}"? This action cannot be undone.`,
            code: record.categoryCode,
          })}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={loading}>
          {t('actions.cancel')}
        </Button>
        <PermissionButton resource={PERMISSION_RESOURCE} action="delete">
          <Button variant="contained" color="error" onClick={handleDelete} disabled={loading}>
            {t('actions.delete')}
          </Button>
        </PermissionButton>
      </DialogActions>
    </>
  );
};

// ── Category Row ──────────────────────────────────────────────────────────────

type CategoryActions = {
  onAddChild: (parent: NotificationCategory) => void;
  onEdit: (row: NotificationCategory) => void;
  onDelete: (row: NotificationCategory) => void;
  /** Called after a drag has persisted a new sibling order. */
  onArranged: () => Promise<void>;
};

/** A category whose expand panel lists its sub-categories, each editable in the same way. */
const CategoryRow = ({
  row,
  childrenOf,
  open,
  onToggle,
  actions,
}: {
  row: NotificationCategory;
  childrenOf: Map<string, NotificationCategory[]>;
  open: boolean;
  onToggle: () => void;
  actions: CategoryActions;
}) => {
  const t = useTranslate();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
  });
  const dragStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const children = childrenOf.get(row.id) ?? [];

  return (
    <>
      <TableRow hover ref={setNodeRef} style={dragStyle}>
        <TableCell sx={{ width: 48 }}>
          <IconButton size="small" onClick={onToggle}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontFamily: 'monospace' }}>{row.categoryCode}</TableCell>
        <TableCell>{row.categoryName}</TableCell>
        {/* <TableCell>
          <Chip
            label={row.kind}
            size="small"
            variant="outlined"
            color={KIND_COLOR[row.kind] ?? 'default'}
          />
        </TableCell> */}
        <TableCell>
          <Chip
            label={children.length}
            size="small"
            variant="outlined"
            color={children.length ? 'primary' : 'default'}
          />
        </TableCell>
        <TableCell>{row.description ? row.description : '-'}</TableCell>
        <TableCell sx={{ minWidth: 140 }}>
          <Chip
            label={row.defaultValue ? 'true' : 'false'}
            size="small"
            color={row.defaultValue ? 'success' : 'default'}
            variant="outlined"
          />
        </TableCell>
        <TableCell>
          <Chip
            label={row.enabled ? 'ON' : 'OFF'}
            size="small"
            color={row.enabled ? 'success' : 'default'}
          />
        </TableCell>
        <TableCell>{row.version}</TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 120 }}>
          <Tooltip title={row.updatedAt} arrow>
            <span>{new Date(row.updatedAt).toLocaleDateString()}</span>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={1} alignItems="center">
            {/* Dragging persists a new sibling order via POST /notification-configs/categories/arrange. */}
            <PermissionButton resource={PERMISSION_RESOURCE} action="update">
              <Tooltip
                title={t('module.notificationConfigs.dragToReorder', { _: 'Drag to reorder' })}
                arrow
              >
                <IconButton
                  size="small"
                  sx={{ cursor: 'grab', color: 'text.disabled' }}
                  {...attributes}
                  {...listeners}
                >
                  <DragIndicatorIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </PermissionButton>
            <Button size="small" variant="outlined" onClick={() => actions.onEdit(row)}>
              {t('actions.edit')}
            </Button>
            <PermissionButton resource={PERMISSION_RESOURCE} action="delete">
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => actions.onDelete(row)}
              >
                {t('actions.delete')}
              </Button>
            </PermissionButton>
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0, borderBottom: open ? undefined : 'none' }} colSpan={COLUMN_COUNT}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Typography variant="subtitle2">
                  {t('module.notificationConfigs.subCategories', { _: 'Sub-categories' })}
                </Typography>
                <PermissionButton resource={PERMISSION_RESOURCE} action="create">
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => actions.onAddChild(row)}
                  >
                    {t('module.notificationConfigs.addSubCategory', { _: 'Add sub-category' })}
                  </Button>
                </PermissionButton>
              </Stack>
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                <CategoryTable
                  rows={children}
                  childrenOf={childrenOf}
                  actions={actions}
                  emptyText={t('module.notificationConfigs.noSubCategories', {
                    _: 'No sub-categories yet',
                  })}
                />
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ── Category Table ────────────────────────────────────────────────────────────

/**
 * One level of the category tree. Each level has its own drag context, so dragging only ever
 * reorders siblings and cannot reparent a category.
 */
const CategoryTable = ({
  rows,
  childrenOf,
  actions,
  loading = false,
  emptyText,
}: {
  rows: NotificationCategory[];
  childrenOf: Map<string, NotificationCategory[]>;
  actions: CategoryActions;
  loading?: boolean;
  emptyText?: string;
}) => {
  const t = useTranslate();
  const notify = useNotify();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [moving, setMoving] = useState(false);

  const sensors = useSensors(
    // A small threshold keeps row buttons clickable without starting a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  /** Reorder a category among its siblings and persist the new sibling order. */
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = rows.findIndex((i) => i.id === active.id);
    const newIndex = rows.findIndex((i) => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(rows, oldIndex, newIndex);

    setMoving(true);
    try {
      await arrangeNotificationCategories({
        items: reordered.map((item, i) => ({
          id: item.id,
          parentId: item.parentId ?? null,
          sortOrder: i,
        })),
      });
      await actions.onArranged();
    } catch (err: any) {
      notify(err?.message ?? t('ra.notification.http_error'), { type: 'error' });
    } finally {
      setMoving(false);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
    >
      <Box sx={{ overflow: 'auto', opacity: moving ? 0.6 : 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 48 }} />
              <TableCell sx={{ fontWeight: 600 }}>
                {t('module.notificationConfigs.categoryCode', { _: 'Category Code' })}
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                {t('module.notificationConfigs.categoryName', { _: 'Category Name' })}
              </TableCell>
              {/* <TableCell sx={{ fontWeight: 600 }}>
                {t('module.notificationConfigs.kind', { _: 'Kind' })}
              </TableCell> */}
              <TableCell sx={{ fontWeight: 600 }}>
                {t('module.notificationConfigs.subCategories', { _: 'Sub-categories' })}
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                {t('module.notificationConfigs.description', { _: 'Description' })}
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                {t('module.notificationConfigs.defaultValue', { _: 'Default' })}
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                {t('module.notificationConfigs.enabled', { _: 'Enabled' })}
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                {t('module.notificationConfigs.version', { _: 'Version' })}
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                {t('module.notificationConfigs.updatedAt', { _: 'Updated At' })}
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary" variant="body2">
                    {t('common.loading', { _: 'Loading...' })}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary" variant="body2">
                    {emptyText ?? t('ra.navigation.no_results')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              <SortableContext items={rows.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                {rows.map((row) => (
                  <CategoryRow
                    key={row.id}
                    row={row}
                    childrenOf={childrenOf}
                    open={expandedId === row.id}
                    onToggle={() => setExpandedId((prev) => (prev === row.id ? null : row.id))}
                    actions={actions}
                  />
                ))}
              </SortableContext>
            )}
          </TableBody>
        </Table>
      </Box>
    </DndContext>
  );
};

// ── Panel ─────────────────────────────────────────────────────────────────────

export const CategoriesPanel = () => {
  const t = useTranslate();
  const [items, setItems] = useState<NotificationCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createParentId, setCreateParentId] = useState<string | null>(null);
  const [editRecord, setEditRecord] = useState<NotificationCategory | null>(null);
  const [deleteRecord, setDeleteRecord] = useState<NotificationCategory | null>(null);

  // The whole tree is loaded at once: paging it would split sub-categories from their parents.
  // `silent` refreshes data in place (no full-table loading state) so an expanded row stays put.
  const fetch = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      setItems(sortCategoryTree(await listAllNotificationCategories()));
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  // `items` is in tree order, so each parent's children come out already sorted by sortOrder.
  // A category whose parent is missing is shown at the top level rather than dropped.
  const { roots, childrenOf } = useMemo(() => {
    const ids = new Set(items.map((i) => i.id));
    const top: NotificationCategory[] = [];
    const byParent = new Map<string, NotificationCategory[]>();
    items.forEach((item) => {
      if (item.parentId && ids.has(item.parentId)) {
        byParent.set(item.parentId, [...(byParent.get(item.parentId) ?? []), item]);
      } else {
        top.push(item);
      }
    });
    return { roots: top, childrenOf: byParent };
  }, [items]);

  const actions: CategoryActions = {
    onAddChild: (parent) => {
      setCreateParentId(parent.id);
      setCreateOpen(true);
    },
    onEdit: setEditRecord,
    onDelete: setDeleteRecord,
    onArranged: () => fetch(true),
  };

  return (
    <Card sx={{ mt: 2 }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <PermissionButton resource={PERMISSION_RESOURCE} action="create">
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={() => {
              setCreateParentId(null);
              setCreateOpen(true);
            }}
            sx={{ minHeight: 40 }}
          >
            {t('module.notificationConfigs.createCategory', { _: 'Create Category' })}
          </Button>
        </PermissionButton>
      </Box>

      <Card>
        <CategoryTable rows={roots} childrenOf={childrenOf} actions={actions} loading={loading} />
      </Card>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="md" fullWidth>
        <CategoryFormDialog
          mode="create"
          categories={items}
          defaultParentId={createParentId}
          onClose={() => setCreateOpen(false)}
          onSaved={() => fetch(true)}
        />
      </Dialog>

      <Dialog
        open={Boolean(editRecord)}
        onClose={() => setEditRecord(null)}
        maxWidth="md"
        fullWidth
      >
        {editRecord && (
          <CategoryFormDialog
            mode="edit"
            record={editRecord}
            categories={items}
            onClose={() => setEditRecord(null)}
            onSaved={() => fetch(true)}
          />
        )}
      </Dialog>

      <Dialog
        open={Boolean(deleteRecord)}
        onClose={() => setDeleteRecord(null)}
        maxWidth="xs"
        fullWidth
      >
        {deleteRecord && (
          <DeleteConfirmDialog
            record={deleteRecord}
            onClose={() => setDeleteRecord(null)}
            onDeleted={() => fetch(true)}
          />
        )}
      </Dialog>
    </Card>
  );
};
